import { matchAny } from '@tunasong/models'
import type { TunaPlugin } from '@tunasong/plugin-lib'
import type { Entity } from '@tunasong/schemas'
import { isFolderContainer } from '@tunasong/schemas'

/**
 * Drop into a container (GH #3164 §3, kind B), the one rule for list rows and cards:
 * - `none`: the target is not a container (or nothing is dragged, or the item is over itself), so it is no drop target;
 * - `accept`: the target's plugin takes the dragged type, and the user can write to both;
 * - `refuse`: a container that won't take it, by type or by write access. It says so and takes no drop.
 */
export type ContainerDropState = 'none' | 'accept' | 'refuse'

const pluginOf = (target: Entity, plugins: TunaPlugin[] | null | undefined) =>
  plugins?.find(plugin => plugin.node?.type === target.type)

/** A folder, space or band, or any entity whose plugin declares children */
export const isContainerTarget = (target: Entity, plugins: TunaPlugin[] | null | undefined) =>
  matchAny(isFolderContainer, () => Boolean(pluginOf(target, plugins)?.allowChildrenTypes))(target)

/** The target's plugin takes this type of child, by its `allowChildrenTypes` */
export const takesChildType = (source: Entity, target: Entity, plugins: TunaPlugin[] | null | undefined) => {
  const allowChildrenTypes = pluginOf(target, plugins)?.allowChildrenTypes
  return typeof allowChildrenTypes === 'boolean'
    ? allowChildrenTypes
    : Boolean(source.type && allowChildrenTypes?.includes(source.type))
}

export const containerDropState = ({
  source,
  target,
  plugins,
  canWrite,
}: {
  source: Entity | null | undefined
  target: Entity
  plugins: TunaPlugin[] | null | undefined
  /** Whether the user can write to an entity: both the dragged item and the target must be writable */
  canWrite: (entity: Entity) => boolean
}): ContainerDropState => {
  if (!source?.type || source.id === target.id || !isContainerTarget(target, plugins)) {
    return 'none'
  }
  return takesChildType(source, target, plugins) && canWrite(source) && canWrite(target) ? 'accept' : 'refuse'
}

const nameOf = (entity: Entity) => entity.name ?? entity.type

/** The confirm before a move (decision 1), with the question mark outside the quotes */
export const moveConfirmText = (source: Entity, target: Entity) =>
  `Move ${source.type} "${nameOf(source)}" to ${target.type} "${nameOf(target)}"?`

/** The caption inside a target that will take the drop */
export const moveCaption = (target: Entity) => `Move to ${nameOf(target)}`

/** The caption inside a container that won't */
export const refuseCaption = (target: Entity) => `You can't add to ${nameOf(target)}`
